import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2,
  ShieldCheck, KeyRound, ArrowLeft, MessageCircle, Mail,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_WHATSAPP = '5581981739909';

const passwordChecks = (pwd) => ({
  length: pwd.length >= 8,
  letter: /[A-Za-z]/.test(pwd),
  digit: /\d/.test(pwd),
});

const whatsappUrl = (email) => {
  const txt = email
    ? `Olá, sou usuário do Tô de Olho. Não recebi o código de recuperação. E-mail cadastrado: ${email}.`
    : 'Olá, sou usuário do Tô de Olho. Preciso de ajuda para recuperar minha senha.';
  return `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(txt)}`;
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Allow ?email= and ?codigo= prefill (e.g., from a future deep link).
  const [email, setEmail] = useState((params.get('email') || '').toLowerCase());
  const [codigo, setCodigo] = useState(params.get('codigo') || '');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentMsg, setResentMsg] = useState('');

  const checks = useMemo(() => passwordChecks(pwd), [pwd]);
  const allGood = checks.length && checks.letter && checks.digit;
  const matches = pwd && confirm && pwd === confirm;
  const codeValid = /^\d{6}$/.test(codigo.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!emailValid) { setError('Informe um e-mail válido.'); return; }
    if (!codeValid) { setError('O código deve ter 6 dígitos.'); return; }
    if (!allGood) { setError('A senha não atende aos requisitos mínimos.'); return; }
    if (!matches) { setError('As senhas não coincidem.'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        codigo: codigo.trim(),
        nova_senha: pwd,
      }, { timeout: 12000 });
      setSuccess(true);
      setTimeout(() => navigate('/auth', { replace: true }), 2500);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (status === 429) setError('Muitas tentativas. Aguarde alguns instantes.');
      else if (status >= 500) setError('Instabilidade no servidor. Tente novamente em instantes.');
      else if (err.code === 'ECONNABORTED') setError('O servidor demorou para responder. Tente novamente.');
      else if (err.message === 'Network Error') setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      else setError('Não foi possível redefinir a senha. Solicite um novo código.');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!emailValid) { setError('Informe um e-mail válido para reenviar o código.'); return; }
    setResending(true); setResentMsg(''); setError('');
    try {
      await axios.post(`${API}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(), usar_recuperacao: false,
      }, { timeout: 10000 });
      setResentMsg('Se o e-mail estiver cadastrado, um novo código foi enviado.');
    } catch {
      setError('Não foi possível reenviar agora. Tente novamente em instantes.');
    } finally { setResending(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4" data-testid="reset-success">
        <Card className="max-w-md w-full p-6 bg-white border border-[#006030] rounded-md text-center">
          <CheckCircle2 className="w-14 h-14 text-[#006030] mx-auto mb-3" />
          <h1 className="text-lg font-black text-[#0F1C2E] mb-2">Senha redefinida!</h1>
          <p className="text-sm text-[#475569] mb-4">
            Sua nova senha já está ativa. Você será redirecionado para o login em instantes.
          </p>
          <Button onClick={() => navigate('/auth', { replace: true })} className="bg-[#006030] hover:bg-[#004020] text-white">
            Ir para o login agora
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4 py-8" data-testid="reset-page">
      <Card className="max-w-md w-full p-6 sm:p-8 bg-white border border-[#E2E8F0] rounded-md">
        <div className="text-center mb-5">
          <div className="inline-flex w-12 h-12 bg-[#E8F5E9] rounded-full items-center justify-center mb-2">
            <KeyRound className="w-5 h-5 text-[#006030]" />
          </div>
          <h1 className="text-xl font-black text-[#0F1C2E]">Redefinir senha</h1>
          <p className="text-xs text-[#475569] mt-1">
            Digite o <strong>código de 6 dígitos</strong> recebido por e-mail (válido por 15 min) e crie sua nova senha.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs text-[#475569]">E-mail</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="pl-9"
                autoComplete="email"
                required
                data-testid="reset-email"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#475569]">Código (6 dígitos)</Label>
            <Input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className="mt-1 text-center text-lg tracking-[0.5em] font-mono font-black"
              required
              data-testid="reset-codigo"
            />
          </div>

          <div>
            <Label className="text-xs text-[#475569]">Nova senha</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
              <Input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="pl-9 pr-10"
                autoComplete="new-password"
                required
                data-testid="reset-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(s => !s)}
                className="absolute right-3 top-2.5 text-[#94A3B8] hover:text-[#475569]"
                aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
                data-testid="reset-toggle-visibility"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {pwd && (
            <ul className="text-[11px] space-y-0.5 pl-1" data-testid="reset-checks">
              <Check ok={checks.length} label="Pelo menos 8 caracteres" />
              <Check ok={checks.letter} label="Pelo menos 1 letra" />
              <Check ok={checks.digit} label="Pelo menos 1 número" />
            </ul>
          )}

          <div>
            <Label className="text-xs text-[#475569]">Confirmar senha</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
              <Input
                type={showPwd ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repita a senha"
                className={`pl-9 ${confirm && !matches ? 'border-[#C62828]' : ''}`}
                autoComplete="new-password"
                required
                data-testid="reset-confirm"
              />
            </div>
            {confirm && !matches && (
              <p className="text-[10px] text-[#C62828] mt-1" data-testid="reset-mismatch">
                As senhas não coincidem.
              </p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-2 bg-[#FFEBEE] border border-[#C62828]/30 rounded-md" data-testid="reset-error">
              <AlertCircle className="w-4 h-4 text-[#C62828] shrink-0 mt-0.5" />
              <p className="text-xs text-[#C62828]">{error}</p>
            </div>
          )}

          {resentMsg && (
            <div className="flex items-start gap-2 p-2 bg-[#F0FDF4] border border-[#BBF7D0] rounded-md" data-testid="reset-resent">
              <CheckCircle2 className="w-4 h-4 text-[#006030] shrink-0 mt-0.5" />
              <p className="text-xs text-[#006030]">{resentMsg}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !codeValid || !emailValid || !allGood || !matches}
            className="w-full bg-[#006030] hover:bg-[#004020] text-white font-medium h-10"
            data-testid="reset-submit"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redefinindo…</>
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-2" /> Redefinir senha</>
            )}
          </Button>
        </form>

        <div className="text-center mt-3">
          <button onClick={resendCode} disabled={resending}
                  className="text-[11px] text-[#475569] hover:text-[#006030] underline disabled:opacity-50"
                  data-testid="reset-resend">
            {resending ? 'Reenviando…' : 'Não recebi o código. Reenviar'}
          </button>
        </div>

        {/* WhatsApp fallback */}
        <div className="flex items-center gap-2 my-4">
          <div className="flex-1 h-px bg-[#E2E8F0]" />
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-wide">ou</span>
          <div className="flex-1 h-px bg-[#E2E8F0]" />
        </div>
        <a href={whatsappUrl(email)} target="_blank" rel="noopener noreferrer"
           className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-3 py-2.5 rounded text-sm"
           data-testid="reset-whatsapp">
          <MessageCircle className="w-4 h-4" /> Recuperar via WhatsApp
        </a>
        <p className="text-[10px] text-[#94A3B8] text-center mt-1.5">
          Suporte humano em horário comercial.
        </p>

        <div className="text-center mt-4">
          <Link to="/auth" className="text-[11px] text-[#475569] hover:text-[#006030] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Voltar ao login
          </Link>
        </div>
      </Card>
    </div>
  );
};

const Check = ({ ok, label }) => (
  <li className={`flex items-center gap-1 ${ok ? 'text-[#006030]' : 'text-[#94A3B8]'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 inline-block rounded-full border border-[#94A3B8]" />}
    <span>{label}</span>
  </li>
);

export default ResetPassword;
